import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  MAIL_DRIVERS,
  MAIL_DRIVER_LABELS,
  OPEN_TRACKING_NOTE,
  formatDateTime,
  messages,
  type IntegrationStatus,
  type MailDriver,
  type OrgSettings,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/common/page';

type MailConfig = {
  driver: MailDriver;
  fromAddress: string;
  fromName: string;
  replyTo: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
};

const EMPTY: MailConfig = {
  driver: 'resend',
  fromAddress: '',
  fromName: '',
  replyTo: '',
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
};

/** S76 メール送信（admin のみ）。フォームが無い相手にメールで送るための設定 */
export const SettingsMailPage = () => {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<MailConfig>(EMPTY);
  const [apiKey, setApiKey] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [tracking, setTracking] = useState({
    htmlMail: true,
    trackOpens: false,
    trackClicks: false,
    unsubscribeLink: true,
  });
  // 文面末尾の署名と、テンプレートの {{sender_*}} {{scheduling_url}} に入る値
  const [sender, setSender] = useState<OrgSettings['sender']>({
    companyName: '',
    personName: '',
    personKana: '',
    phone: '',
    postalCode: '',
    address: '',
    contact: '',
    unsubscribeNotice: '',
    schedulingUrl: '',
    newsletterUrl: '',
  });

  const { data: integrations, isLoading } = useQuery({
    queryKey: qk.integrations,
    queryFn: () => api.get<SingleResponse<IntegrationStatus[]>>('/settings/integrations'),
  });

  const { data: settings } = useQuery({
    queryKey: qk.settings,
    queryFn: () => api.get<SingleResponse<{ settings: OrgSettings }>>('/settings'),
  });

  const mail = integrations?.data.find((i) => i.provider === 'mail_send');

  useEffect(() => {
    if (!mail) return;
    const stored = mail.config as Partial<MailConfig>;
    setConfig({ ...EMPTY, ...stored });
  }, [mail]);

  useEffect(() => {
    if (!settings) return;
    const s = settings.data.settings.send;
    setTracking({
      htmlMail: s.htmlMail,
      trackOpens: s.trackOpens,
      trackClicks: s.trackClicks,
      unsubscribeLink: s.unsubscribeLink,
    });
    const stored = settings.data.settings.sender;
    setSender({ ...stored, schedulingUrl: stored.schedulingUrl ?? '', newsletterUrl: stored.newsletterUrl ?? '' });
  }, [settings]);

  const save = useMutation({
    mutationFn: async () => {
      await api.put('/settings/integrations/mail_send', {
        ...(apiKey ? { apiKey } : {}),
        config: {
          ...config,
          smtpPort: Number(config.smtpPort),
          ...(smtpPassword ? { smtpPassword } : {}),
        },
      });
      await api.patch('/settings', { send: tracking, sender });
    },
    onSuccess: () => {
      toast.success(messages.common.saved);
      setApiKey('');
      setSmtpPassword('');
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
      void queryClient.invalidateQueries({ queryKey: qk.settings });
      void queryClient.invalidateQueries({ queryKey: qk.mailAccount });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ ok: boolean; message: string }>>(
        '/settings/integrations/mail_send/test',
        testTo ? { to: testTo } : {},
      ),
    onSuccess: (result) => {
      if (result.data.ok) {
        toast.success(messages.settings.testSucceeded, { description: result.data.message });
      } else {
        toast.error(messages.settings.testFailed, { description: result.data.message });
      }
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading) return <SkeletonRows rows={5} />;

  const isResend = config.driver === 'resend';

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.mailSend}
        description="フォームが無い企業にはメールで送ります。差出人と送信方法をここで設定します。"
      />

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail aria-hidden className="size-5" />
              送信方法
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label={messages.settings.mailDriver} required>
              {(props) => (
                <Select
                  {...props}
                  value={config.driver}
                  onChange={(e) => setConfig((p) => ({ ...p, driver: e.target.value as MailDriver }))}
                >
                  {MAIL_DRIVERS.map((driver) => (
                    <option key={driver} value={driver}>
                      {MAIL_DRIVER_LABELS[driver]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {isResend && (
              <Field
                label={messages.settings.resendApiKey}
                hint={
                  mail?.maskedKey
                    ? `${messages.settings.keyMasked}（現在：${mail.maskedKey}）`
                    : messages.settings.resendNote
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="off"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="re_..."
                  />
                )}
              </Field>
            )}

            {config.driver === 'smtp' && (
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label={messages.settings.smtpHost}>
                  {(props) => (
                    <Input
                      {...props}
                      value={config.smtpHost}
                      onChange={(e) => setConfig((p) => ({ ...p, smtpHost: e.target.value }))}
                    />
                  )}
                </Field>
                <Field label={messages.settings.smtpPort}>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      value={config.smtpPort}
                      onChange={(e) => setConfig((p) => ({ ...p, smtpPort: Number(e.target.value) }))}
                    />
                  )}
                </Field>
                <Field label={messages.settings.smtpUser}>
                  {(props) => (
                    <Input
                      {...props}
                      autoComplete="off"
                      value={config.smtpUser}
                      onChange={(e) => setConfig((p) => ({ ...p, smtpUser: e.target.value }))}
                    />
                  )}
                </Field>
                <Field label={messages.settings.smtpPassword} hint={messages.settings.keyMasked}>
                  {(props) => (
                    <Input
                      {...props}
                      type="password"
                      autoComplete="off"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                    />
                  )}
                </Field>
                <label className="flex items-center gap-2 text-body">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={config.smtpSecure}
                    onChange={(e) => setConfig((p) => ({ ...p, smtpSecure: e.target.checked }))}
                  />
                  {messages.settings.smtpSecure}
                </label>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>差出人</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label={messages.settings.fromAddress}
              required
              hint={isResend ? messages.settings.resendNote : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={config.fromAddress}
                  onChange={(e) => setConfig((p) => ({ ...p, fromAddress: e.target.value }))}
                  placeholder="sales@example.co.jp"
                />
              )}
            </Field>
            <Field label={messages.settings.fromName}>
              {(props) => (
                <Input
                  {...props}
                  value={config.fromName}
                  onChange={(e) => setConfig((p) => ({ ...p, fromName: e.target.value }))}
                  placeholder="株式会社ネスク 営業部"
                />
              )}
            </Field>
            <Field label={messages.settings.replyTo} hint="空欄なら差出人アドレスに返信されます">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={config.replyTo}
                  onChange={(e) => setConfig((p) => ({ ...p, replyTo: e.target.value }))}
                />
              )}
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{messages.settings.signature}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-note text-foreground-muted">{messages.settings.signatureNote}</p>
            <Field label={messages.settings.senderCompany} required>
              {(props) => (
                <Input
                  {...props}
                  value={sender.companyName}
                  onChange={(e) => setSender((p) => ({ ...p, companyName: e.target.value }))}
                  placeholder="株式会社ネスク"
                />
              )}
            </Field>
            <Field label={messages.settings.senderPerson}>
              {(props) => (
                <Input
                  {...props}
                  value={sender.personName}
                  onChange={(e) => setSender((p) => ({ ...p, personName: e.target.value }))}
                  placeholder="営業担当"
                />
              )}
            </Field>
            <Field label="担当者フリガナ（フォーム用）" hint="問い合わせフォームにフリガナ欄があれば差し込みます">
              {(props) => (
                <Input
                  {...props}
                  value={sender.personKana}
                  onChange={(e) => setSender((p) => ({ ...p, personKana: e.target.value }))}
                  placeholder="マツドヒカリ"
                />
              )}
            </Field>
            <Field label="電話番号（フォーム用）" hint="フォームに電話欄があれば差し込みます">
              {(props) => (
                <Input
                  {...props}
                  value={sender.phone}
                  onChange={(e) => setSender((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="050-1794-0897"
                />
              )}
            </Field>
            <Field label="郵便番号（フォーム用）" hint="フォームに郵便番号欄があれば差し込みます">
              {(props) => (
                <Input
                  {...props}
                  value={sender.postalCode}
                  onChange={(e) => setSender((p) => ({ ...p, postalCode: e.target.value }))}
                  placeholder="141-0021"
                />
              )}
            </Field>
            <Field label="住所（フォーム用）" hint="フォームに住所欄があれば差し込みます">
              {(props) => (
                <Input
                  {...props}
                  value={sender.address}
                  onChange={(e) => setSender((p) => ({ ...p, address: e.target.value }))}
                  placeholder="東京都品川区上大崎3丁目14番34号プラスワン402"
                />
              )}
            </Field>
            <Field label={messages.settings.senderContact} required>
              {(props) => (
                <Input
                  {...props}
                  value={sender.contact}
                  onChange={(e) => setSender((p) => ({ ...p, contact: e.target.value }))}
                  placeholder="sales@example.co.jp"
                />
              )}
            </Field>
            <Field label={messages.settings.schedulingUrl} hint={messages.settings.schedulingUrlNote}>
              {(props) => (
                <Input
                  {...props}
                  type="url"
                  value={sender.schedulingUrl}
                  onChange={(e) => setSender((p) => ({ ...p, schedulingUrl: e.target.value }))}
                  placeholder="https://example.com/schedule"
                />
              )}
            </Field>
            <Field label={messages.settings.newsletterUrl} hint={messages.settings.newsletterUrlNote}>
              {(props) => (
                <Input
                  {...props}
                  type="url"
                  value={sender.newsletterUrl}
                  onChange={(e) => setSender((p) => ({ ...p, newsletterUrl: e.target.value }))}
                  placeholder="https://example.com/newsletter"
                />
              )}
            </Field>
            <Field label={messages.settings.unsubscribeNotice}>
              {(props) => (
                <Textarea
                  {...props}
                  value={sender.unsubscribeNotice}
                  onChange={(e) => setSender((p) => ({ ...p, unsubscribeNotice: e.target.value }))}
                />
              )}
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{messages.settings.tracking}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Toggle
              label={messages.settings.htmlMail}
              hint={messages.settings.htmlMailNote}
              checked={tracking.htmlMail}
              onChange={(htmlMail) => setTracking((p) => ({ ...p, htmlMail }))}
            />
            <Toggle
              label={messages.settings.trackOpens}
              hint={OPEN_TRACKING_NOTE}
              checked={tracking.trackOpens}
              disabled={!tracking.htmlMail}
              onChange={(trackOpens) => setTracking((p) => ({ ...p, trackOpens }))}
            />
            <Toggle
              label={messages.settings.trackClicks}
              hint="本文中のリンクを計測用 URL に置き換えます。表示される文字は元の URL のままです。"
              checked={tracking.trackClicks}
              disabled={!tracking.htmlMail}
              onChange={(trackClicks) => setTracking((p) => ({ ...p, trackClicks }))}
            />
            <Toggle
              label={messages.settings.unsubscribeLink}
              hint={messages.settings.unsubscribeLinkNote}
              checked={tracking.unsubscribeLink}
              onChange={(unsubscribeLink) => setTracking((p) => ({ ...p, unsubscribeLink }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send aria-hidden className="size-5" />
              接続の確認
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="テスト送信先" hint="空欄なら差出人アドレス宛に送ります">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder={config.fromAddress}
                />
              )}
            </Field>
            <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
              {messages.settings.testConnection}
            </Button>
            {mail?.lastTestedAt && (
              <p className="text-note text-foreground-muted">
                {messages.settings.lastTestedAt}{' '}
                <span className="tabular">{formatDateTime(mail.lastTestedAt)}</span>
                {mail.lastTestResult === 'success' ? '（成功）' : '（失敗）'}
                {mail.lastTestMessage && <>：{mail.lastTestMessage}</>}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {messages.common.save}
          </Button>
        </div>
      </div>
    </>
  );
};

const Toggle = ({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) => (
  <label className="flex items-start gap-3">
    <input
      type="checkbox"
      className="mt-1 size-4 shrink-0"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="min-w-0">
      <span className="block text-body font-semibold">{label}</span>
      {hint && <span className="block text-note text-foreground-muted">{hint}</span>}
    </span>
  </label>
);
